import React from 'react';
import { motion } from 'framer-motion';
import { 
  AcademicCapIcon, 
  CodeBracketIcon,
  ChartBarIcon,
  CloudIcon,
  CpuChipIcon,
  GlobeAltIcon,
  UsersIcon,
  ClockIcon,
  CheckCircleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import { personStructuredData } from '../data/seo';

const About = () => {
  const stats = [
    { label: 'Students Taught', value: '500+', icon: UsersIcon },
    { label: 'Years Experience', value: '7+', icon: ClockIcon },
    { label: 'Countries Reached', value: '25+', icon: GlobeAltIcon },
    { label: 'Success Rate', value: '95%', icon: CheckCircleIcon },
  ];

  const expertise = [
    { icon: CodeBracketIcon, name: 'Python Programming', description: 'From basics to advanced frameworks' },
    { icon: GlobeAltIcon, name: 'Web Development', description: 'React, Django, and modern web technologies' },
    { icon: ChartBarIcon, name: 'Data Analysis', description: 'Pandas, NumPy, and statistical analysis' },
    { icon: CpuChipIcon, name: 'Machine Learning', description: 'Algorithms, model training, and deployment' },
    { icon: CloudIcon, name: 'DevOps & Cloud', description: 'Docker, Kubernetes, AWS, and CI/CD' },
    { icon: AcademicCapIcon, name: 'Career Guidance', description: 'Portfolio building and job search support' },
  ];

  const values = [
    {
      icon: LightBulbIcon,
      title: 'Personalized Learning',
      description: 'Every student is unique. I adapt my teaching style to match your learning pace and preferences.'
    },
    {
      icon: UsersIcon,
      title: 'Student-Centered Approach',
      description: 'Your goals and interests drive the curriculum. We focus on what matters most to your career.'
    },
    {
      icon: CheckCircleIcon,
      title: 'Practical Experience',
      description: 'Learn through real-world projects and hands-on coding exercises that build your portfolio.'
    },
    {
      icon: GlobeAltIcon,
      title: 'Global Accessibility',
      description: 'Quality education should be accessible to everyone, regardless of location or background.'
    }
  ];

  const timeline = [
    {
      year: '2015',
      title: 'Started Teaching',
      description: 'Began offering programming mentorship to friends and colleagues'
    },
    {
      year: '2020',
      title: 'Online Transition',
      description: 'Moved to online teaching during the pandemic, reaching students globally'
    },
    {
      year: '2021',
      title: 'Course Development',
      description: 'Created structured curriculum for Python, React, and Data Analysis'
    },
    {
      year: '2022',
      title: 'DevOps Expansion',
      description: 'Added DevOps and Cloud computing courses to the curriculum'
    },
    {
      year: '2023',
      title: 'AI Integration',
      description: 'Incorporated Machine Learning and AI topics into teaching programs'
    },
    {
      year: '2024',
      title: 'Platform Launch',
      description: 'Launched TeachWithDuke platform for streamlined learning experience'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO
        title="About Duke - Software Engineer & Programming Mentor"
        description="Meet Duke, an experienced software engineer and educator with 7+ years of teaching experience. Learn about his journey helping 500+ students worldwide transition into tech careers through personalized programming mentorship."
        keywords="duke software engineer, programming mentor, coding instructor, software development teacher, tech career coach, python expert, react developer, devops specialist, machine learning instructor"
        canonical="/about"
        ogImage="/og-about.jpg"
        structuredData={personStructuredData}
      />
      {/* Header */}
      <section className="bg-gradient-to-r from-indigo-700 to-cyan-600 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">
              About Duke
            </h1>
            <p className="text-xl md:text-2xl text-cyan-100 max-w-3xl mx-auto">
              Passionate educator and technology expert dedicated to helping students 
              achieve their programming goals through personalized mentorship.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-20 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-6">
                My Story
              </h2>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <p>
                  With over 7 years of experience in software development and education, 
                  I've dedicated my career to making programming accessible and enjoyable 
                  for learners of all levels.
                </p>
                <p>
                  My journey began when I started helping friends and colleagues learn to code. 
                  I quickly realized that personalized, one-on-one instruction was far more 
                  effective than traditional classroom settings or online courses.
                </p>
                <p>
                  Today, I've helped over 500 students worldwide transition into tech careers, 
                  from complete beginners to experienced professionals looking to upskill. 
                  My approach combines technical expertise with patience, encouragement, 
                  and real-world project experience.
                </p>
                <p>
                  I believe that anyone can learn to code with the right guidance and support. 
                  My mission is to provide that support and help you achieve your goals, 
                  whether that's landing your first tech job or advancing your current career.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-indigo-600 to-cyan-500 rounded-2xl p-8 text-white">
                <div className="text-center">
                  <div className="w-32 h-32 bg-white/20 rounded-full mx-auto mb-6 flex items-center justify-center">
                    <span className="text-6xl">👨‍💻</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Duke</h3>
                  <p className="text-cyan-100 mb-6">
                    Software Engineer & Educator
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold">7+</div>
                      <div className="text-sm text-cyan-100">Years Teaching</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">500+</div>
                      <div className="text-sm text-cyan-100">Students</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 dark:bg-indigo-900 rounded-full mb-4">
                  <stat.icon className="h-8 w-8 text-indigo-600 dark:text-cyan-300" />
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-20 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-4">
              Areas of Expertise
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Comprehensive knowledge across multiple technology domains
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {expertise.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 dark:bg-slate-700 rounded-2xl p-6 border border-slate-200 dark:border-slate-600 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <area.icon className="h-8 w-8 text-indigo-600 dark:text-cyan-300 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {area.name}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  {area.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-4">
              Teaching Philosophy
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              The principles that guide my approach to education
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
                      <value.icon className="h-6 w-6 text-indigo-600 dark:text-cyan-300" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {value.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-4">
              Teaching Journey
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              A timeline of my evolution as an educator
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-indigo-200 dark:bg-indigo-800"></div>
            
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative flex items-center mb-8 ${
                  index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}
              >
                <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                  <div className="bg-white dark:bg-slate-700 rounded-lg p-6 border border-slate-200 dark:border-slate-600 shadow-md">
                    <div className="text-indigo-600 dark:text-cyan-300 font-bold text-lg mb-2">
                      {item.year}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {item.description}
                    </p>
                  </div>
                </div>
                
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-indigo-600 rounded-full border-4 border-white dark:border-slate-800"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-indigo-700 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold mb-6">
              Ready to Start Your Learning Journey?
            </h2>
            <p className="text-xl text-cyan-100 mb-8">
              Let's work together to achieve your programming goals. 
              Book a free consultation to discuss your learning path.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/booking"
                className="inline-flex items-center px-6 py-3 bg-white text-indigo-700 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-200 transform hover:scale-[1.02]"
              >
                Book Free Consultation
              </a>
              <a
                href="/contact"
                className="inline-flex items-center px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-indigo-700 transition-all duration-200"
              >
                Get In Touch
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
